<template>
  <DialogPrime
    v-model:visible="localVisible"
    :modal="true"
    :dismissable-mask="false"
    header="Check Duplicate"
    style="width: 1100px; max-width: 95vw;"
  >
    <div class="row g-3">
      <!-- Left Card - Materials by description similarity -->
      <div v-if="!isUserRole" class="col-6">
        <div class="card mb-0" style="height: 480px;">
          <div class="card-header py-2">
            <h6 class="mb-0">Long Desc Materials</h6>
          </div>
          <div class="card-body p-0" style="height: 400px; overflow-y: auto;">
            <div v-if="leftCard.loading" class="text-center py-5 text-secondary">
              <i class="fas fa-spinner fa-spin me-2"></i> Loading...
            </div>
            <table v-else-if="leftCard.data.length" class="table table-sm table-hover mb-0">
              <thead class="table-primary" style="position: sticky; top: 0; z-index: 1;">
                <tr>
                  <th class="text-uppercase text-dark text-xxs font-weight-bolder" style="width: 36px;"></th>
                  <th class="text-uppercase text-dark text-xxs font-weight-bolder">Material Detail</th>
                  <!-- <th class="text-uppercase text-dark text-xxs font-weight-bolder" style="width: 150px;">Info</th> -->
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in leftCard.data" :key="row.id">
                  <td class="align-middle text-center py-2 px-2">
                    <Checkbox
                      :model-value="isLeftChecked(row)"
                      :binary="true"
                      :disabled="saving"
                      @update:model-value="onToggleLeft(row)"
                    />
                  </td>
                  <td class="align-top py-2 px-2">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <div style="flex: 1; height: 5px; border-radius: 3px; background-color: #e9ecef; overflow: hidden;">
                        <div :style="{ width: (row.similarity * 100).toFixed(0) + '%', height: '100%', backgroundColor: row.similarity >= 0.8 ? '#f5365c' : '#fb6340', borderRadius: '3px' }"></div>
                      </div>
                      <span class="text-xxs font-weight-bolder" style="white-space: nowrap;" :style="{ color: row.similarity >= 0.8 ? '#f5365c' : '#fb6340' }">
                        {{ (row.similarity * 100).toFixed(0) }}% match
                      </span>
                    </div>
                    <div class="d-flex align-items-center mb-1">
                      <span class="badge bg-primary-soft text-primary font-weight-bolder me-2 ps-0 pe-2" style="font-size: 11px;">
                        {{ formatMaterialNumber(row.material_number) }}
                      </span>
                    </div>
                    <div class="text-xxs text-dark" style="font-family: 'Courier New', Courier, monospace; word-break: break-all; white-space: normal;">
                      {{ row.long_description }}
                    </div>
                    <div class="text-xxs text-dark">
                      <span class="text-xxs font-weight-bolder text-dark">{{ row.company || '-' }}</span> | <span class="text-xxs font-weight-bolder text-dark">{{ row.material_type || '-' }}</span> | <span class="text-xxs font-weight-bolder text-dark">{{ row.material_group || '-' }}</span> | <span class="text-xxs font-weight-bolder text-dark">{{ row.uom || '-' }}</span>
                    </div>
                  </td>
                  <!-- <td class="align-top py-2 px-2">
                    <div class="d-flex flex-column gap-1">
                      <div v-if="row.company"><span class="field-label me-1">Company</span><span class="text-xxs font-weight-bolder text-dark">{{ row.company || '-' }}</span></div>
                      <div><span class="field-label me-1">MType</span><span class="text-xxs font-weight-bolder text-dark">{{ row.material_type_description || '-' }}</span></div>
                      <div><span class="field-label me-1">MGrp</span><span class="text-xxs font-weight-bolder text-dark">{{ row.material_group_description || '-' }}</span></div>
                      <div><span class="field-label me-1">UoM</span><span class="text-xxs font-weight-bolder text-dark">{{ row.uom_description || '-' }}</span></div>
                    </div>
                  </td> -->
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center text-muted" style="padding: 60px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 100px; opacity: 0.7;" />
              <div style="font-size: 0.9em;">No Data</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Card - Materials by manufacturer / part number (SPN) match -->
      <div :class="isUserRole ? 'col-12' : 'col-6'">
        <div class="card mb-0" style="height: 480px;">
          <div class="card-header py-2">
            <h6 class="mb-0">Manufacturer / Part Number Matches</h6>
          </div>
          <div class="card-body p-0" style="height: 400px; overflow-y: auto;">
            <div v-if="rightCard.loading" class="text-center py-5 text-secondary">
              <i class="fas fa-spinner fa-spin me-2"></i> Loading...
            </div>
            <DataTable
              v-else
              :value="rightCard.data"
              v-model:expandedRows="expandedRows"
              dataKey="id"
              class="p-datatable-sm"
            >
              <Column expander style="width: 2.5rem" />
              <Column header="Manufacturer Info">
                <template #body="{ data }">
                  <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <span v-if="data.duplicate_count" class="badge bg-secondary text-xxs">
                      {{ data.duplicate_count }} duplicate{{ data.duplicate_count > 1 ? 's' : '' }}
                    </span>
                  </div>
                  <div class="d-flex gap-3 text-xxs text-dark font-weight-bolder flex-wrap">
                    <span><span class="field-label">Mfr</span> {{ data.manufacture || '-' }}</span>
                    <span><span class="field-label">SPN</span> {{ data.spn || '-' }}</span>
                  </div>
                </template>
              </Column>
              <template #expansion="{ data }">
                <div class="p-2 bg-light">
                  <div
                    v-for="dup in data.duplicates"
                    :key="dup.id"
                    class="duplicate-card-premium p-2 border-radius-lg border bg-white mb-2 shadow-sm d-flex align-items-center gap-2"
                  >
                    <Checkbox
                      v-if="!isUserRole"
                      :model-value="isRightChecked(dup)"
                      :binary="true"
                      :disabled="saving"
                      @update:model-value="onToggleRight(dup)"
                    />
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span class="badge bg-gray-100 text-dark text-xxs font-weight-bolder ps-0 pe-2">
                          M/N: {{ formatMaterialNumber(dup.material_number) }}
                        </span>
                        <span v-if="dup.company_description" class="text-xxs text-secondary font-weight-bold">
                          {{ dup.company_description }}
                        </span>
                      </div>
                      <div class="d-flex gap-2 text-xxs text-dark font-weight-bolder flex-wrap">
                        <span><span class="field-label">MType</span> {{ dup.material_type_description || '-' }}</span>
                        <span><span class="field-label">MGrp</span> {{ dup.material_group_description || '-' }}</span>
                      </div>
                      <div class="d-flex text-xxs text-dark font-weight-bolder flex-wrap">
                        <span><span class="field-label">UoM</span> {{ dup.uom_description || '-' }}</span>
                      </div>
                      <div class="text-xxs text-dark mt-1" style="font-family: 'Courier New', Courier, monospace; word-break: break-all; white-space: normal;">
                        {{ dup.long_description }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template #empty>
                <div class="text-center text-muted" style="padding: 60px 0;">
                  <img src="/no_data.png" alt="No Data" style="max-width: 100px; opacity: 0.7;" />
                  <div style="font-size: 0.9em;">No Data</div>
                </div>
              </template>
            </DataTable>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <ButtonPrime label="Close" outlined :disabled="saving" @click="localVisible = false" />
      <ButtonPrime v-if="!isUserRole" label="Save" :loading="saving" @click="onSaveDuplicates" />
    </template>
  </DialogPrime>
</template>

<script>
import { mapState, mapActions } from "vuex";
import api from "@/helpers/api";
import cataloguerFormService from "@/services/materialCatalogingRequest/cataloguerFormService";

const getCompanyCode = () => {
  try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user?.company_code || ''; } catch { return ''; }
};

export default {
  name: "CheckDuplicateModal",
  props: {
    visible: { type: Boolean, default: false },
    mcrItem: { type: Object, default: null },
    rowNumber: { type: Number, default: null },
  },
  emits: ["update:visible", "saved"],
  data() {
    return {
      expandedRows: {},
      saving: false,
      pendingDuplicatedWith: { "duplicated-material": [], "duplicated-mpn": [] },
    };
  },
  computed: {
    ...mapState({
      checkDuplicateDialog: (state) => state.mcr.checkDuplicateDialog,
      userHasRoles: (state) => state.auth.userHasRoles || [],
      currentUsername: (state) => state.auth.user?.namecode || '',
    }),
    isUserRole() {
      return this.userHasRoles.some(role => role === 'User');
    },
    localVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
    leftCard() {
      return this.checkDuplicateDialog.leftCard;
    },
    rightCard() {
      return this.checkDuplicateDialog.rightCard;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.pendingDuplicatedWith = {
          "duplicated-material": [...(this.mcrItem?.dupl_material_number?.["duplicated-material"] || [])],
          "duplicated-mpn": [...(this.mcrItem?.dupl_material_number?.["duplicated-mpn"] || [])],
        };
        this.loadDuplicates();
      } else {
        this.expandedRows = {};
      }
    },
    // Auto-expand baris yang salah satu duplicate-nya sudah ter-checked (isRightChecked).
    'rightCard.data'(rows) {
      const expanded = {};
      (rows || []).forEach((row) => {
        if ((row.duplicates || []).some((dup) => this.isRightChecked(dup))) {
          expanded[row.id] = true;
        }
      });
      this.expandedRows = expanded;
    },
  },
  methods: {
    ...mapActions("mcr", ["processDuplicateDescription", "processDuplicatePartNumber"]),
    // Jalankan pengecekan description & part_number sekaligus berdasarkan mcrItem yang sedang dibuka
    async loadDuplicates() {
      this.$store.commit('mcr/resetCheckDuplicateDialog');

      const longDesc = this.mcrItem?.cat_po_horizontal || '';
      const spnList = [...new Set(
        (this.mcrItem?.cat_emcr_item_manufacturer || [])
          .map((m) => m.spn)
          .filter(Boolean)
      )];

      const tasks = [];
      if (longDesc && !this.isUserRole) tasks.push(this.fetchDuplicateDescription(longDesc));
      if (spnList.length) tasks.push(this.fetchDuplicatePartNumber(spnList));

      await Promise.all(tasks);
    },
    async fetchDuplicateDescription(longDesc) {
      this.$store.commit('mcr/setCheckDuplicateLoading', { card: 'leftCard', value: true });
      try {
        const res = await api(this, cataloguerFormService.getDuplicationInItem('description', this.withCompanyIfApplicable({ long_desc: longDesc })), false);
        const rows = this.extractDuplicateRows(res).filter((row) => !this.isSelfMaterial(row.material_number));
        await this.processDuplicateDescription(rows);
      } catch (error) {
        this.$toastNotifPrime('error', 'Error', error?.response?.data?.message || 'Failed to fetch duplicate by description');
        // disimpan di vuex mcr
        await this.processDuplicateDescription([]);
      } finally {
        this.$store.commit('mcr/setCheckDuplicateLoading', { card: 'leftCard', value: false });
      }
    },
    async fetchDuplicatePartNumber(spnList) {
      this.$store.commit('mcr/setCheckDuplicateLoading', { card: 'rightCard', value: true });
      try {
        const res = await api(this, cataloguerFormService.getDuplicationInItem('part_number_array', this.withCompanyIfApplicable({ spn_array: spnList })), false);
        // material_number-nya ada di dalam row.duplicates[], bukan di row itu sendiri — filter di situ
        // lalu buang row yang jadi kosong (grup manufacture/SPN yang duplikatnya cuma item ini sendiri)
        const rows = this.extractDuplicateRows(res)
          .map((row) => {
            // filter detektor hanya return dup.materialNumber !== this.mcrItem.material_number
            const duplicates = (row.duplicates || []).filter((dup) => !this.isSelfMaterial(dup.material_number));
            return { ...row, duplicates, duplicate_count: duplicates.length };
          })
          .filter((row) => row.duplicates.length > 0);
        // disimpan di vuex mcr
        await this.processDuplicatePartNumber(rows);
      } catch (error) {
        this.$toastNotifPrime('error', 'Error', error?.response?.data?.message || 'Failed to fetch duplicate by part number');
        await this.processDuplicatePartNumber([]);
      } finally {
        this.$store.commit('mcr/setCheckDuplicateLoading', { card: 'rightCard', value: false });
      }
    },
    isSelfMaterial(materialNumber) {
      return !!this.mcrItem?.material_number && materialNumber === this.mcrItem.material_number;
    },
    // Bentuk response bisa array polos, atau Laravel paginator: { data: { data: [...] } }
    extractDuplicateRows(res) {
      const payload = res?.data ?? res ?? [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      return [];
    },
    withCompanyIfApplicable(payload) {
      const companyCode = getCompanyCode();
      if (['INALUM', 'TIMAH', 'ANTAM'].includes(companyCode)) {
        return { ...payload, company: companyCode };
      }
      return payload;
    },
    isLeftChecked(row) {
      return this.pendingDuplicatedWith["duplicated-material"].includes(row.material_number);
    },
    isRightChecked(dup) {
      return this.pendingDuplicatedWith["duplicated-mpn"].includes(dup.manufacture_material_number);
    },
    toggleDuplicateEntry(key, materialNumber) {
      const list = this.pendingDuplicatedWith[key];
      const idx = list.indexOf(materialNumber);
      if (idx === -1) {
        list.push(materialNumber);
      } else {
        list.splice(idx, 1);
      }
    },
    onToggleLeft(row) {
      this.toggleDuplicateEntry("duplicated-material", row.material_number);
    },
    onToggleRight(dup) {
      this.toggleDuplicateEntry("duplicated-mpn", dup.manufacture_material_number);
    },
    async onSaveDuplicates() {
      const matList = this.pendingDuplicatedWith["duplicated-material"];
      const mpnList = this.pendingDuplicatedWith["duplicated-mpn"];
      const total = matList.length + mpnList.length;

      const duplicateNotePrefix = 'This MCR Item has been marked as a duplicate because';
      const unDuplicateNotePrefix = 'This MCR Item has been unmarked as a duplicate because';
      const notes = this.mcrItem?.emcr_item_note || [];
      const nullStatusNotes = notes.filter((n) => n.status === null);
      const pendingDuplicateNotes = nullStatusNotes.filter((n) => (n.notes || '').startsWith(duplicateNotePrefix));
      const latestByCreatedAt = (arr) => [...arr].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      let scenario;
      if (total > 0) {
        // Ada baris dipilih -> selalu (re-)mark, apapun status/note yang sudah ada sebelumnya.
        scenario = 'markWithReason';
      } else if (this.mcrItem?.status === 'duplicated') {
        if (nullStatusNotes.length === 0) {
          // Sudah fully marked (tidak ada note pending) -> aksi ini adalah unmark dengan alasan.
          scenario = 'unmarkWithReason';
        } else if (pendingDuplicateNotes.length === 0) {
          // Tidak ada note duplicate null yang pending (null yang ada milik fitur lain) -> jangan proses.
          return;
        } else {
          scenario = 'unmarkRemovePending';
        }
      } else {
        // Belum pernah ditandai duplicate dan tidak ada baris dipilih -> tidak ada yang perlu disimpan.
        return;
      }

      const showInput = scenario === 'markWithReason' || scenario === 'unmarkWithReason';
      const message = scenario === 'markWithReason'
        ? `This item will be marked as duplicate for ${total} row${total > 1 ? 's' : ''} (${matList.length} by description, ${mpnList.length} by MPN).`
        : `All duplicate marks for this item will be removed.`;
      const result = await this.confirmCancelItemDialog(scenario !== 'markWithReason', 'duplicate', message, showInput);
      if (!result.isConfirmed) return;
      let emcrItemNote = notes;
      if (scenario === 'unmarkRemovePending') {
        const latestPendingDuplicateNote = latestByCreatedAt(pendingDuplicateNotes);
        emcrItemNote = notes.filter((n) => n !== latestPendingDuplicateNote);
      } else {
        const reason = (result.value || '').trim();
        const normalizedReason = reason.charAt(0).toLowerCase() + reason.slice(1);
        const prefix = scenario === 'unmarkWithReason' ? unDuplicateNotePrefix : duplicateNotePrefix;
        const baseNotes = scenario === 'markWithReason'
          ? notes.filter((n) => !(n.status === null && ((n.notes || '').startsWith(duplicateNotePrefix) || (n.notes || '').startsWith(unDuplicateNotePrefix))))
          : notes;
        emcrItemNote = [
          ...baseNotes,
          {
            id: null,
            mcr_item_code: this.mcrItem?.mcr_item_code || '',
            notes: `${prefix} ${normalizedReason}`,
            status: null,
            created_by: this.currentUsername,
            updated_by: this.currentUsername,
            created_at: null,
            updated_at: null,
          },
        ];
      }

      await this.updateStatus('duplicated', { ...this.pendingDuplicatedWith }, emcrItemNote);
    },
    async updateStatus(status, duplicated_with, emcrItemNote) {
      if (!this.mcrItem?.mcr_item_code) return false;
      this.saving = true;
      try {
        const response = await api(this, cataloguerFormService.updateItemStatus(this.mcrItem.mcr_item_code, { status, duplicated_with, old_status: this.mcrItem.status, obj_catalogued_by: this.mcrItem.catalogued_by || {}, action_notes: 'update_notes', emcr_item_note: emcrItemNote }));
        this.$emit('saved', response.data);
        return true;
      } catch (error) {
        const httpStatus = error?.response?.status;
        const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
        const detail = serverMsg || error?.message || 'Unknown error';
        const summary = httpStatus ? `Error ${httpStatus}` : 'Error';
        this.$toastNotifPrime('error', summary, detail);
        return false;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  color: #8898aa;
  letter-spacing: 0.3px;
  margin-right: 4px;
}

.duplicate-card-premium {
  border-left: 4px solid #dee2e6 !important;
}

:deep(.p-datatable-thead > tr > th) {
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
